import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { ELIMINATIONS_CHOICES, StepText } from './types';
import { TABS } from '../constants';
import { getConsolidationDetailUrl, getCreateConsolidationUrl } from '@futrli/shared-services';
import { useConsolidationContext } from '../consolidation-context/consolidation-context';
import { hasEnoughOrganisations } from '../consolidation-context/utils';
import { hasDuplicateValues } from './utils';

interface ConsolidationWizardContextData {
  currentStepNum: number;
  eliminationsChoice?: ELIMINATIONS_CHOICES;
  handleBack: () => void;
  handleNext: () => void;
  isBackEnabled: boolean;
  isNextEnabled: boolean;
  isSaving: boolean;
  progress: number;
  setEliminationsChoice: (choice: ELIMINATIONS_CHOICES) => void;
  stepText?: StepText;
}

interface ConsolidationWizardContextProps {
  children?: ReactNode;
}

const ConsolidationWizardContext = createContext<ConsolidationWizardContextData>(
  ({} as unknown) as ConsolidationWizardContextData
);

const useContextSetup = () => {
  const { workspaceId: tempWorkspaceId, tabId: tempTabId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const tabId = tempTabId || '';
  const navigate = useNavigate();
  const intl = useIntl();
  const [eliminationsChoice, setEliminationsChoice] = useState<ELIMINATIONS_CHOICES>();
  const [isBackEnabled, setIsBackEnabled] = useState(false);
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const [stepText, setStepText] = useState<StepText>();
  const {
    settings: {
      selectedOrganisations,
      settingsForm: { formState: settingsFormState, watch },
      orgCodeMap
    },
    eliminations: { eliminationGroups },
    flags: { isMultiCurrency, isSaving },
    handleSubmit
  } = useConsolidationContext();

  const TEXT: Record<string, StepText> = useMemo(
    () => ({
      [TABS.SETTINGS]: {
        title: intl.formatMessage({
          id: 'consolidation.wizard.settings_title',
          defaultMessage: 'Add your entities to the consolidation...'
        }),
        desc: intl.formatMessage({
          id: 'consolidation.wizard.settings_description',
          defaultMessage:
            'Give the consolidation a name, choose its year end date and presentation currency. Then add all of the entities to it.'
        })
      },
      [TABS.ELIMINATIONS_QUESTION]: {
        title: intl.formatMessage({
          id: 'consolidation.wizard.eliminations_question_title',
          defaultMessage: 'Do you need to eliminate any accounts?'
        }),
        desc: intl.formatMessage({
          id: 'consolidation.wizard.eliminations_question_description',
          defaultMessage:
            'Eliminations allow you to remove the impact of transactions between companies in a group, resulting in a more accurate view of consolidated performance. One typical use of an elimination would be to account for intercompany loans or intercompany management fees within a group.'
        })
      },
      [TABS.ELIMINATIONS]: {
        title: intl.formatMessage({
          id: 'consolidation.wizard.eliminations_title',
          defaultMessage: 'Which accounts do you want to eliminate?'
        }),
        desc: intl.formatMessage({
          id: 'consolidation.wizard.eliminations_description',
          defaultMessage:
            "Group one or more accounts into eliminated groups. Futrli calculates the unbalanced transactions, where the transactions don't balance to zero, and automatically creates a newly calculated offset account in reports and budgets or forecasts."
        })
      },
      [TABS.COA_STRUCTURE]: {
        title: intl.formatMessage({
          id: 'consolidation.wizard.coa_structure_title',
          defaultMessage: 'What reporting structure would you like for your consolidation?'
        }),
        desc: intl.formatMessage({
          id: 'consolidation.wizard.coa_structure_description',
          defaultMessage: 'The structure you choose will be reflected in every report and budget, forecast or scenario.'
        })
      },
      [TABS.DAILY_FX_RATES]: {
        title: intl.formatMessage({
          id: 'consolidation.wizard.daily_fx_rates_title',
          defaultMessage: 'These are the FX rates that will be applied.'
        }),
        desc: intl.formatMessage({
          id: 'consolidation.wizard.daily_fx_rates_description',
          defaultMessage:
            "Futrli uses Sage's daily FX rate conversions within your consolidation. You can override these for days and ranges below."
        })
      }
    }),
    [intl]
  );

  const totalSteps = Object.keys(TABS).length - (isMultiCurrency ? 0 : 1);
  const currentStepNum = Object.values(TABS).indexOf(tabId as TABS) || 0;
  const progress = (currentStepNum * 100) / (totalSteps - 1);

  const settingsFields = watch();

  const isSettingsFormValid = useCallback(() => {
    return (
      !hasDuplicateValues(orgCodeMap) &&
      settingsFormState.isValid &&
      !!settingsFields.currency &&
      !!settingsFields.name &&
      !!settingsFields.yearEnd &&
      hasEnoughOrganisations(selectedOrganisations, settingsFields.currency)
    );
  }, [
    orgCodeMap,
    selectedOrganisations,
    settingsFields.currency,
    settingsFields.name,
    settingsFields.yearEnd,
    settingsFormState.isValid
  ]);

  const isEliminationsFormValid = useCallback(() => Object.keys(eliminationGroups).length > 0, [eliminationGroups]);

  // ************
  // * Handlers *
  // ************

  const handleBack = useCallback(() => {
    switch (tabId) {
      case TABS.ELIMINATIONS_QUESTION: {
        navigate(getCreateConsolidationUrl(workspaceId, TABS.SETTINGS));
        break;
      }
      case TABS.ELIMINATIONS: {
        navigate(getCreateConsolidationUrl(workspaceId, TABS.ELIMINATIONS_QUESTION));
        break;
      }
      case TABS.COA_STRUCTURE: {
        if (eliminationsChoice === ELIMINATIONS_CHOICES.YES) {
          navigate(getCreateConsolidationUrl(workspaceId, TABS.ELIMINATIONS));
        } else {
          navigate(getCreateConsolidationUrl(workspaceId, TABS.ELIMINATIONS_QUESTION));
        }
        break;
      }
      case TABS.DAILY_FX_RATES: {
        navigate(getCreateConsolidationUrl(workspaceId, TABS.COA_STRUCTURE));
        break;
      }
      default: {
        navigate(getCreateConsolidationUrl(workspaceId, TABS.SETTINGS));
        break;
      }
    }
  }, [eliminationsChoice, navigate, tabId, workspaceId]);

  const handleFinish = useCallback(async () => {
    try {
      setIsBackEnabled(false);
      setIsNextEnabled(false);
      const response = await handleSubmit();
      if (response) {
        navigate(getConsolidationDetailUrl(workspaceId, response.id!));
      }
    } finally {
      setIsBackEnabled(true);
      setIsNextEnabled(true);
    }
  }, [handleSubmit, navigate, workspaceId]);

  const handleNext = useCallback(() => {
    switch (tabId) {
      case TABS.SETTINGS: {
        navigate(getCreateConsolidationUrl(workspaceId, TABS.ELIMINATIONS_QUESTION));
        break;
      }
      case TABS.ELIMINATIONS_QUESTION: {
        if (eliminationsChoice === ELIMINATIONS_CHOICES.YES) {
          navigate(getCreateConsolidationUrl(workspaceId, TABS.ELIMINATIONS));
        } else {
          navigate(getCreateConsolidationUrl(workspaceId, TABS.COA_STRUCTURE));
        }
        break;
      }
      case TABS.ELIMINATIONS: {
        navigate(getCreateConsolidationUrl(workspaceId, TABS.COA_STRUCTURE));
        break;
      }
      case TABS.COA_STRUCTURE: {
        if (isMultiCurrency) {
          navigate(getCreateConsolidationUrl(workspaceId, TABS.DAILY_FX_RATES));
        } else {
          handleFinish();
        }
        break;
      }
      case TABS.DAILY_FX_RATES: {
        handleFinish();
        break;
      }
      default: {
        navigate(getCreateConsolidationUrl(workspaceId, TABS.SETTINGS));
        break;
      }
    }
  }, [eliminationsChoice, handleFinish, navigate, isMultiCurrency, tabId, workspaceId]);

  // ***********
  // * Effects *
  // ***********

  // Set if back / next is enabled
  useEffect(() => {
    if (tabId === TABS.SETTINGS) {
      setIsNextEnabled(isSettingsFormValid());
    } else if (tabId === TABS.ELIMINATIONS) {
      setIsNextEnabled(isEliminationsFormValid());
    } else {
      setIsNextEnabled(true);
    }

    setIsBackEnabled(tabId !== TABS.SETTINGS);
  }, [isEliminationsFormValid, isSettingsFormValid, tabId]);

  // Set the stepText variable when current tab changes
  useEffect(() => {
    setStepText(TEXT[tabId]);
  }, [TEXT, tabId]);

  // Check that we have settings data upon loading a page. If not, boot back to settings
  useEffect(() => {
    if (tabId !== TABS.SETTINGS && !isSettingsFormValid()) {
      navigate(getCreateConsolidationUrl(workspaceId, TABS.SETTINGS), { replace: true });
    }
  }, [navigate, isSettingsFormValid, tabId, workspaceId]);

  return {
    currentStepNum,
    eliminationsChoice,
    handleBack,
    handleNext,
    isBackEnabled,
    isNextEnabled,
    isSaving,
    progress,
    setEliminationsChoice,
    stepText
  };
};

export const ConsolidationWizardContextProvider = ({ children }: ConsolidationWizardContextProps) => {
  const value = useContextSetup();
  return <ConsolidationWizardContext.Provider value={value}>{children}</ConsolidationWizardContext.Provider>;
};

export const useConsolidationWizardContext = () => useContext(ConsolidationWizardContext);
